import type { TFunction } from 'i18next';

import { httpStatusOf, isProblemDetailCode } from 'src/common/ProblemDetail';

import type { LookalikeComparisonDto, RangeDto, RangeStatus } from './ensan-steckbrief.dto';
import { LOOKALIKE_REFERENCE_KEYS } from './steckbrief-variant-fields';

/**
 * The distinct outcome behind a failed lookalike search (D4) — kept as a pure helper so the
 * mapping is unit-tested directly. kein Treffer is NOT here: it is a normal `NO_MATCH` result.
 * The backend sends an errorCode (LOOKALIKE_*); a bare ProblemDetail body still carries its status.
 */
export type LookalikeErrorCode =
    | 'LOOKALIKE_INDEX_UNAVAILABLE'
    | 'LOOKALIKE_TIMEOUT'
    | 'LOOKALIKE_SEARCH_FAILED'
    | 'LOOKALIKE_QUEUE_FULL';

export function lookalikeErrorCode(error: unknown): LookalikeErrorCode {
    if (isProblemDetailCode(error, 'LOOKALIKE_INDEX_UNAVAILABLE')) return 'LOOKALIKE_INDEX_UNAVAILABLE';
    if (isProblemDetailCode(error, 'LOOKALIKE_TIMEOUT')) return 'LOOKALIKE_TIMEOUT';
    if (isProblemDetailCode(error, 'LOOKALIKE_QUEUE_FULL')) return 'LOOKALIKE_QUEUE_FULL';
    const status = httpStatusOf(error);
    if (status === 503) return 'LOOKALIKE_INDEX_UNAVAILABLE';
    if (status === 504) return 'LOOKALIKE_TIMEOUT';
    return 'LOOKALIKE_SEARCH_FAILED';
}

/** Maps a FAILED lookalike job's `errorCode` (from the backend) to a frontend error code. */
export function lookalikeJobErrorCode(errorCode: string | null): LookalikeErrorCode {
    switch (errorCode) {
        case 'LOOKALIKE_INDEX_UNAVAILABLE':
            return 'LOOKALIKE_INDEX_UNAVAILABLE';
        case 'LOOKALIKE_TIMEOUT':
            return 'LOOKALIKE_TIMEOUT';
        case 'LOOKALIKE_QUEUE_FULL':
            return 'LOOKALIKE_QUEUE_FULL';
        default:
            return 'LOOKALIKE_SEARCH_FAILED';
    }
}

export function lookalikeErrorMessage(t: TFunction, code: LookalikeErrorCode): string {
    switch (code) {
        case 'LOOKALIKE_INDEX_UNAVAILABLE':
            return t('The reference index is unavailable. Please try again later.');
        case 'LOOKALIKE_TIMEOUT':
            return t('The search timed out. Please try again.');
        case 'LOOKALIKE_QUEUE_FULL':
            return t('Too many searches are running. Please try again in a moment.');
        default:
            return t('The search did not succeed. Please try again.');
    }
}

const BOUND_EPSILON = 1e-6;

/**
 * Client-side twin of the backend `LookalikeComparisonEngine.rangeStatus`, for the Investitionskosten/m²
 * row: the range is the advisor's live one, so the backend cannot flag it at search time. INSIDE within
 * the range, NEAR within [tolerance] (the comparison's `nearRelativeTolerance` — the backend owns the
 * threshold) of the range magnitude of the nearest bound, OUTSIDE beyond. Null when any input is missing.
 */
export function rangeStatus(
    range: RangeDto | null | undefined,
    actual: number | null | undefined,
    tolerance: number | null | undefined,
): RangeStatus | null {
    if (!range || actual === null || actual === undefined || tolerance === null || tolerance === undefined) {
        return null;
    }
    const margin = Math.max(Math.abs(range.low), Math.abs(range.high)) * tolerance;
    const distance = Math.min(Math.abs(actual - range.low), Math.abs(actual - range.high));
    if (actual >= range.low - BOUND_EPSILON && actual <= range.high + BOUND_EPSILON) return 'INSIDE';
    if (distance <= margin + BOUND_EPSILON) return 'NEAR';
    return 'OUTSIDE';
}

/**
 * The form-value patch that stamps a Gebäudevergleich as the Variant's lookalike reference context
 * (GEN-700 D4 lineage) — every value copied from the server response, never typed. A NO_MATCH
 * result keeps only its search id: it supersedes an earlier comparison, so the old shortlist and
 * fingerprint are cleared. The comparison itself is kept as JSON text (`lookalikeComparison`) so the
 * panel shows it again when the Variant — or the next one — is opened; it is display only and never
 * enters the Prognose.
 */
export function lookalikeReferencePatch(result: LookalikeComparisonDto): Record<string, unknown> {
    const ok = result.status === 'OK';
    const candidates = ok ? [...result.candidates].sort((a, b) => a.rank - b.rank) : [];
    return {
        [LOOKALIKE_REFERENCE_KEYS.searchId]: result.searchId,
        [LOOKALIKE_REFERENCE_KEYS.indexVersion]: ok ? (result.versions?.index ?? null) : null,
        [LOOKALIKE_REFERENCE_KEYS.docIds]: ok ? candidates.map((candidate) => candidate.docId) : null,
        [LOOKALIKE_REFERENCE_KEYS.packageIds]: ok ? candidates.map((candidate) => candidate.packageId ?? '') : null,
        [LOOKALIKE_REFERENCE_KEYS.fingerprint]: ok ? result.matchFingerprint : null,
        [LOOKALIKE_REFERENCE_KEYS.comparison]: JSON.stringify(result),
    };
}

/**
 * The comparison a Variant keeps (`lookalikeComparison`, JSON text) — null when absent or not a
 * comparison (a Variant saved before it was kept, or a malformed value), so the panel starts empty.
 */
export function stampedComparison(value: unknown): LookalikeComparisonDto | null {
    if (typeof value !== 'string' || value.trim() === '') return null;
    try {
        const parsed: unknown = JSON.parse(value);
        if (typeof parsed !== 'object' || parsed === null) return null;
        const candidate = parsed as Partial<LookalikeComparisonDto>;
        const valid =
            (candidate.status === 'OK' || candidate.status === 'NO_MATCH') &&
            Array.isArray(candidate.candidates) &&
            Array.isArray(candidate.prognoseBand) &&
            Array.isArray(candidate.subject);
        return valid ? (parsed as LookalikeComparisonDto) : null;
    } catch {
        return null;
    }
}
